/**
 * resolveRuntimeValues.ts
 *
 * Replaces `/* @runtimeValues <expression> *\/` annotations in TypeScript
 * type-source text with actual runtime union-type literals.
 *
 * ── How it works ────────────────────────────────────────────────────────────
 *
 * In AssistantFunctionTypes.ts, reusable type aliases are annotated like this:
 *
 *   /* @runtimeValues APP.metadata.Auswahlwert.filter(x=>x.Auswahl.Key=='Pfostenfarbe').map(x=>x.Key) *\/
 *   type TPfostenfarbe = string;
 *
 * At query-time, `resolveRuntimeValues()` processes the raw source text,
 * evaluates each expression against the live APP global, and replaces the
 * `string` type with a concrete union literal:
 *
 *   type TPfostenfarbe =
 *     | "Anthrazit"
 *     | "Silber"
 *     | "Weiß";
 *
 * This also works for legacy property-level annotations:
 *
 *   /* @runtimeValues <expr> *\/
 *   pfostenfarbe: string;
 *
 * This way the AI always sees the values that are currently available in the
 * configured product database – without any hard-coding in the source file.
 *
 * ── Design decisions ────────────────────────────────────────────────────────
 *
 * • The annotation comment is deliberately placed ABOVE the declaration (not
 *   inline) so that it can be removed from the AI-visible text while keeping
 *   the declaration intact.
 *
 * • `new Function(...)` is used instead of `eval` to keep the expression
 *   in a fresh scope; APP is available as a global, so no arguments needed.
 *
 * • If the expression fails or returns an empty array, the fallback is the
 *   bare `string` type so the AI can still reason about the property.
 *
 * • The function is intentionally pure (string → string) and has no side
 *   effects, making it easy to test.
 */

// ── Annotation regex ─────────────────────────────────────────────────────────

/**
 * Matches a @runtimeValues block followed immediately by a `<prop>: string;`
 * property line (inside interfaces).
 *
 * Capture groups:
 *   1  JS expression to evaluate (everything between @runtimeValues and *\/)
 *   2  Leading whitespace of the property line (used to align union members)
 *   3  Property name (may contain Unicode letters such as ö, ü, ä)
 *
 * Flags:
 *   g  – replace all occurrences in a single pass
 */
const PROP_ANNOTATION_RE =
  /\/\* @runtimeValues ([^\n]+?) \*\/\r?\n(\s+)(\S+): string;/g;

/**
 * Matches a @runtimeValues block followed immediately by a `type <Name> = string;`
 * type-alias declaration.
 *
 * Capture groups:
 *   1  JS expression to evaluate
 *   2  Leading whitespace of the type line
 *   3  Type alias name
 *
 * Flags:
 *   g  – replace all occurrences in a single pass
 */
const TYPE_ANNOTATION_RE =
  /\/\* @runtimeValues ([^\n]+?) \*\/\r?\n(\s*)type (\S+) = string;/g;

// ── Expression evaluator ──────────────────────────────────────────────────────

/**
 * Evaluates a @runtimeValues JavaScript expression and returns the result as
 * a string array.
 *
 * The expression is executed via `new Function` with an optional `context`
 * argument so callers can inject runtime values (e.g. store snapshots)
 * without polluting the global scope.
 *
 * @param expression - The JS expression from the annotation
 * @param context    - Optional object available as `context` inside the expression
 * @returns           Array of strings, or [] on error / unexpected type
 */
function evalExpression(expression: string, context: Record<string, unknown> = {}): string[] {
  try {
    // new Function with 'context' parameter → expression may reference context.xxx
    // eslint-disable-next-line no-new-func
    const result = new Function("context", `return (${expression})`)(context);

    if (!Array.isArray(result)) {
      console.warn(
        `[resolveRuntimeValues] Expression did not return an array.\n` +
          `  Expression: ${expression}\n` +
          `  Got: ${typeof result}`
      );
      return [];
    }

    // Keep only actual strings; filter out null/undefined/objects that may
    // appear when metadata is partially loaded.
    return result.filter((v): v is string => typeof v === "string");
  } catch (e) {
    console.error(
      `[resolveRuntimeValues] Failed to evaluate expression:\n` +
        `  ${expression}\n`,
      e
    );
    return [];
  }
}

// ── Union type builder ────────────────────────────────────────────────────────

/**
 * Formats an array of runtime values as a TypeScript union type fragment.
 *
 * Example with indent = "  " (two spaces):
 *   ["Anthrazit", "Silber", "Weiß"]
 *   →  "\n    | \"Anthrazit\"\n    | \"Silber\"\n    | \"Weiß\""
 *
 * Each pipe character is placed on its own line and indented by the property's
 * own indentation plus two additional spaces (matching the TypeScript style
 * used in the original AssistantFunctionTypes.ts).
 *
 * @param values  Resolved string values from the live metadata
 * @param indent  Indentation prefix of the property line (e.g. "  ")
 * @returns       Union fragment starting with "\n", or " string" as fallback
 */
function buildUnion(values: string[], indent: string): string {
  if (values.length === 0) {
    // Metadata not yet loaded or expression returned nothing → keep bare type.
    // The leading space is intentional: it produces `propName: string;`
    return " string";
  }

  // Each option line gets extra indentation (property indent + 2 spaces)
  const memberIndent = indent + "  ";
  return values.map((v) => `\n${memberIndent}| ${JSON.stringify(v)}`).join("");
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Processes the raw source of AssistantFunctionTypes.ts and expands every
 * `/* @runtimeValues <expr> *\/` annotation into a concrete TypeScript union
 * type, using the values currently available in APP.metadata.
 *
 * Supports two patterns:
 * - Type aliases:  `/* @runtimeValues <expr> *\/ type TFoo = string;`
 * - Properties:    `/* @runtimeValues <expr> *\/ propName: string;`
 *
 * Call this function each time the AI tool description is built so that the
 * AI always sees up-to-date product options without requiring a page reload.
 *
 * @param source  - Raw TypeScript source text (as loaded via import ... ?raw or raw-loader)
 * @param context - Optional object available as `context` inside @runtimeValues expressions.
 *                  Use this to inject live store/metadata values without global variables.
 *                  Example: `resolveRuntimeValues(src, { toppings: pizzaStore.getSnapshot().toppings })`
 * @returns       Transformed source with all @runtimeValues annotations expanded
 *
 * @example
 *   // Vite (recommended):
 *   import apiSource from "./AssistantFunctionTypes.ts?raw";
 *   import { resolveRuntimeValues } from "btg-ai-agent";
 *
 *   const resolvedTypes = resolveRuntimeValues(apiSource, { toppings: TOPPINGS });
 *   // resolvedTypes now contains union types instead of bare `string`
 *
 * @example
 *   // webpack (raw-loader):
 *   import apiSource from "!!raw-loader!./AssistantFunctionTypes.ts";
 */
// Matches any remaining @runtimeValues annotation comment that was not consumed
// by ANNOTATION_RE (e.g. property type is no longer `string`, or unexpected
// whitespace). The leading \s* also swallows the newline before the comment so
// no blank line is left behind.
const LEFTOVER_ANNOTATION_RE = /[ \t]*\/\* @runtimeValues [^\n]+? \*\/\r?\n?/g;

export function resolveRuntimeValues(source: string, context: Record<string, unknown> = {}): string {
  // Pass 1a: expand @runtimeValues annotations on interface properties.
  let result = source.replace(
    PROP_ANNOTATION_RE,
    (_match, expression: string, indent: string, propertyName: string) => {
      const values = evalExpression(expression, context);
      const union = buildUnion(values, indent);
      return `${indent}${propertyName}:${union};`;
    }
  );

  // Pass 1b: expand @runtimeValues annotations on type aliases.
  result = result.replace(
    TYPE_ANNOTATION_RE,
    (_match, expression: string, indent: string, typeName: string) => {
      const values = evalExpression(expression, context);
      const union = buildUnion(values, indent);

      return `${indent}type ${typeName} =${union};`;
    }
  );

  // Pass 2: remove any @runtimeValues comments that slipped through Pass 1.
  // This keeps the AI-visible source free of internal annotation noise.
  result = result.replace(LEFTOVER_ANNOTATION_RE, "");

  // Pass 3: extract only the section between @api-section-start and @api-section-end
  // so the AI only sees the relevant API surface, not imports/exports/internals.
  const sectionStart = result.indexOf("/* @api-section-start */");
  const sectionEnd = result.indexOf("/* @api-section-end */");
  if (sectionStart !== -1 && sectionEnd !== -1) {
    result = result
      .substring(
        sectionStart + "/* @api-section-start */".length,
        sectionEnd
      )
      .trim();
  }

  return result;
}
